// src/pages/NotificationsPage/NotificationGroup.jsx
import React from 'react';
import { notificationsStyles } from './styles';
import NotificationCard from '../../components/NotificationCard';
import { 
  canRespondToNotification, 
  handleNotificationAction, 
  getOtherPartyUserId,
  updateInviteStatus 
} from './notificationUtils';
import { sendNotification } from '../../utils/notifications';
import { formatDateToUS } from '../../utils/dateFormatter';

const NotificationGroup = ({
  groupKey,
  notes,
  isExpanded,
  onToggleExpanded,
  senders,
  user,
  respondingId,
  setRespondingId,
  response,
  setResponse,
  justSuggestedId,
  setNotifications,
  setJustSuggestedId
}) => {

  const handleAccept = async (note) => {
    try {
      // Step 1: Update current notification to 'accepted' with lastActionBy
      await updateInviteStatus(note.id, 'accepted', '', '', '');
      // Step 2: Add calendar event for both users
      // IMPORTANT: Keep original buyer/seller roles - they NEVER switch
      const { addCalendarEvent } = await import('../../utils/calendarUtils');
      const { formatDateToISO } = await import('../../utils/dateFormatter');
      
      // Convert date to ISO format (YYYY-MM-DD) for calendar storage
      const isoDate = formatDateToISO(note.date);
      
      await addCalendarEvent({
        listingId: note.listingId || '',
        buyerId: note.buyerId,   // Use original buyerId - NEVER changes
        sellerId: note.sellerId, // Use original sellerId - NEVER changes
        date: isoDate,  // Store in YYYY-MM-DD format
        time: note.time,
        location: note.location || '',
        title: note.title || 'Meetup'
      });
      // Step 3: Notify the OTHER party that their invite was accepted
    // IMPORTANT: Always preserve original buyer/seller IDs
    await sendNotification({
      recipientId: note.senderId,
      senderId: user.uid,
      type: 'invite',  // Use 'invite' type for consistency
      title: 'Meeting Accepted',
      message: `Your meeting invite for ${formatDateToUS(note.date)} at ${note.time} at ${note.location?.address || ''} was accepted.`,
      date: note.date,
      time: note.time,
      location: note.location,
      status: 'accepted',  // Mark as accepted so no action buttons show
      buyerId: note.buyerId,    // Preserve original buyer - NEVER changes
      sellerId: note.sellerId,  // Preserve original seller - NEVER changes
      listingId: note.listingId,
      lastActionBy: user.uid  // Set lastActionBy to prevent further actions
    });
    
      // Step 4: Update local state
      handleNotificationAction({ 
        setNotifications, 
        setRespondingId, 
        setJustSuggestedId, 
        setResponse, 
        note 
      }, 'accepted');
    } catch (error) {
      alert(`Failed to accept meeting: ${error.message}`);
    }
  };

  const handleReject = async (note) => {
    // Step 1: Update current notification to 'rejected' with lastActionBy
    await updateInviteStatus(note.id, 'rejected', '', '', '');
    
    // Step 2: Notify the OTHER party that their invite was rejected
    // IMPORTANT: Preserve original buyer/seller IDs
    await sendNotification({
      recipientId: note.senderId,
      senderId: user.uid,
      type: 'invite',  // Use 'invite' type for consistency
      title: 'Meeting Rejected',
      message: `Your meeting invite for ${formatDateToUS(note.date)} at ${note.time} at ${note.location?.address || ''} was rejected.`,
      date: note.date,
      time: note.time,
      location: note.location,
      status: 'rejected',  // Mark as rejected so no action buttons show
      buyerId: note.buyerId,    // Preserve original buyer - NEVER changes
      sellerId: note.sellerId,  // Preserve original seller - NEVER changes
      listingId: note.listingId,
      lastActionBy: user.uid  // Set lastActionBy to prevent further actions
    });
    
    // Step 3: Update local state
    handleNotificationAction({ 
      setNotifications, 
      setRespondingId, 
      setJustSuggestedId, 
      setResponse, 
      note 
    }, 'rejected');
  };

  const handleSuggest = async (note, { date, time, address, lat, lng }) => {
    // Get the other user's ID (recipient of the counter-suggestion)
    const recipientUserId = getOtherPartyUserId(note, user.uid);
    
    // Prepare location data - use new location if provided, otherwise use original
    const location = (address && lat && lng) 
      ? { address, lat, lng }
      : note.location;
    
    // Update local state/UI immediately
    handleNotificationAction({ 
      setNotifications, 
      setRespondingId, 
      setJustSuggestedId, 
      setResponse, 
      note, 
      response: { date, time, message: `Suggested new time: ${formatDateToUS(date)} at ${time}` } 
    }, 'suggested');
    
    // Step 1: Update current notification to 'pending' with lastActionBy = current user
    // This removes action buttons for the person who suggested
    // IMPORTANT: Also update the location on the current notification so it displays correctly
    await updateInviteStatus(note.id, 'pending', `Suggested new time: ${formatDateToUS(date)} at ${time}`, date, time, true, location);
    
    // Step 2: Create NEW notification for the other party with the suggested time
    // CRITICAL: Preserve original buyer/seller IDs - they NEVER switch
    // The new notification will show action buttons to the OTHER person
    await sendNotification({
      recipientId: recipientUserId,
      senderId: user.uid,
      type: 'invite',  // Use 'invite' type so it shows as actionable
      title: 'Meeting Invite - New Time Suggested',
      message: `New time suggested: ${formatDateToUS(date)} at ${time}${location?.address ? ' at ' + location.address : ''}`,
      date,
      time,
      location,
      status: 'pending',
      buyerId: note.buyerId,    // Preserve original buyer - NEVER changes
      sellerId: note.sellerId,  // Preserve original seller - NEVER changes
      listingId: note.listingId
    });
  };

  // Get the most recent notification (last in array after sorting)
  const latest = notes[notes.length - 1];
  const latestSender = latest.senderId && senders[latest.senderId];
  
  // Determine the conversation status
  const getConversationStatus = () => {
    if (latest.status === 'accepted') return 'Accepted';
    if (latest.status === 'rejected') return 'Rejected';
    if (notes.length > 1) return `${notes.length} messages`;
    return 'New';
  };

  return (
    <div style={notificationsStyles.groupContainer}>
      <div 
        style={notificationsStyles.groupHeader} 
        onClick={() => onToggleExpanded(groupKey)}
      >
        <div style={notificationsStyles.groupContent}>
          <div style={notificationsStyles.groupTitle}>
            Meeting Invite Thread{' '}
            <span style={notificationsStyles.groupCount}>
              ({getConversationStatus()})
            </span>
          </div>
          
          {latest.senderId && latestSender && (
            <div style={notificationsStyles.senderInfo}>
              Latest from: <strong style={notificationsStyles.senderName}>
                {latestSender.name || latestSender.email || latest.senderId}
              </strong>
            </div>
          )}
          
          <div style={notificationsStyles.groupMessage}>{latest.message}</div>
          
          {latest.location && (
            <div style={notificationsStyles.locationInfo}>
              <span style={notificationsStyles.locationLabel}>Location:</span>{' '}
              {latest.location.address || 'No location'}
            </div>
          )}
          
          <div style={notificationsStyles.expandHint}>
            {isExpanded ? 'Click to collapse' : 'Click to expand and view conversation'}
          </div>
        </div>
        
        <div style={notificationsStyles.expandIcon}>
          {isExpanded ? '▾' : '▸'}
        </div>
      </div>
      
      {/* Expanded: show all notifications in this group */}
      {isExpanded && (
        <div style={notificationsStyles.expandedContent}>
          {notes.map(note => {
            const canRespond = canRespondToNotification(note, user, justSuggestedId);
            
            return (
              <NotificationCard
                key={note.id}
                note={note}
                senders={senders}
                user={user}
                respondingId={respondingId}
                setRespondingId={setRespondingId}
                response={response}
                setResponse={setResponse}
                canRespond={canRespond}
                handleAccept={() => handleAccept(note)}
                handleReject={() => handleReject(note)}
                handleSuggest={(suggestionData) => handleSuggest(note, suggestionData)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationGroup;